import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Eye, FileText, UploadSimple, Download, Archive, PaperPlaneTilt, CalendarBlank, Check, X, UserCircle, ClipboardText, Truck, CheckCircle, Lightning, EnvelopeSimple, Lock, PencilSimple, FilePdf, FileXls, Export, Trash, ChatCircle, ArrowDown, ArrowUp, CaretDown, ArrowSquareIn, ArrowSquareOut } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuSeparator,
} from '@/components/ui/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { uploadFileToGCS } from '../utils/gcsUpload';
import PDFViewerSVG from '../components/PDFViewerSVG';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Human-readable file size (per-revision PDF / native sizes)
const formatFileSize = (bytes) => {
  const b = Number(bytes);
  if (!b || b <= 0) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};


// Review code options
const REVIEW_CODES = {
  code_1: { label: 'Code 1: Approved', color: 'bg-green-100 text-green-800' },
  code_2: { label: 'Code 2: Approved with Comment', color: 'bg-blue-100 text-blue-800' },
  code_3: { label: 'Code 3: Rejected', color: 'bg-red-100 text-red-800' },
  code_4: { label: 'Code 4: For Information Only', color: 'bg-gray-100 text-gray-800' }
};

const ReviewCycles = ({ user }) => {
  const { documentId } = useParams();
  const location = useLocation();
  const [currentDoc, setCurrentDoc] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedCycleForUpload, setSelectedCycleForUpload] = useState(null);
  const [viewCycleId, setViewCycleId] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [pmReviewOpen, setPmReviewOpen] = useState(false);
  const [selectedCycleForPM, setSelectedCycleForPM] = useState(null);
  const [cycleComments, setCycleComments] = useState([]);
  const [selectedReviewCode, setSelectedReviewCode] = useState('');
  const [pmComments, setPmComments] = useState('');
  const [dcConfirmOpen, setDcConfirmOpen] = useState(false);
  const [selectedCycleForDC, setSelectedCycleForDC] = useState(null);
  const [dcComments, setDcComments] = useState('');
  const [emailPreview, setEmailPreview] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailToList, setEmailToList] = useState([]);
  const [emailCcList, setEmailCcList] = useState([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [userAccess, setUserAccess] = useState(null);
  
  // Expanded cycle state for reviewer actions
  const [expandedCycleId, setExpandedCycleId] = useState(null);
  const [reviewerComment, setReviewerComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [finalizingReview, setFinalizingReview] = useState(false);
  
  // Comments Upload State
  const [commentsUploadOpen, setCommentsUploadOpen] = useState(false);
  const [selectedCycleForComments, setSelectedCycleForComments] = useState(null);
  const commentsInputRef = useRef(null);
  
  // Edit Reviewers State
  const [editReviewersOpen, setEditReviewersOpen] = useState(false);
  const [selectedCycleForEdit, setSelectedCycleForEdit] = useState(null);
  const [editReviewers, setEditReviewers] = useState({
    responsible_engineer: '',
    discipline_reviewer: '',
    project_manager: '',
    document_controller: ''
  });
  
  // Edit Revision State (for sysadmin/doc controller)
  const [editRevisionOpen, setEditRevisionOpen] = useState(false);
  const [selectedCycleForRevisionEdit, setSelectedCycleForRevisionEdit] = useState(null);
  const [editRevisionData, setEditRevisionData] = useState({
    revision: '',
    revision_date: '',
    review_status: '',
    review_stage: ''
  });
  
  const pdfInputRef = useRef(null);
  const nativeInputRef = useRef(null);
  const [newCycle, setNewCycle] = useState({
    revision: '',
    revision_date: '',
    classification: '',
    responsible_engineer: '',
    discipline_reviewer: '',
    project_manager: '',
    document_controller: '',
    reviewers: [], // Multiple reviewers
    incoming_transmittal_nr: '',
    incoming_transmittal_date: '',
    incoming_transmittal_id: '',
    outgoing_transmittal_nr: '',
    outgoing_transmittal_date: '',
    outgoing_transmittal_id: '',
    manual_comments: '',
    pdf_file: null,
    native_file: null
  });
  const [transmittals, setTransmittals] = useState([]);

  useEffect(() => {
    if (documentId) {
      fetchDocument();
      fetchCycles();
    }
    fetchUsers();
    fetchTransmittals();
    fetchUserAccess();
  }, [documentId]);

  const fetchUserAccess = async () => {
    try {
      const response = await axios.get(`${API}/users/me/access`);
      setUserAccess(response.data.access);
    } catch (error) {
      console.error('Failed to fetch user access');
    }
  };

  const fetchTransmittals = async () => {
    try {
      const response = await axios.get(`${API}/transmittals`);
      setTransmittals(response.data);
    } catch (error) {
      console.error('Failed to fetch transmittals');
    }
  };

  const fetchDocument = async () => {
    try {
      const response = await axios.get(`${API}/documents/${documentId}`);
      setCurrentDoc(response.data);
    } catch (error) {
      toast.error('Failed to fetch document');
    }
  };

  const fetchCycles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cycles?document_id=${documentId}`);
      setCycles(response.data);
    } catch (error) {
      toast.error('Failed to fetch cycles');
    } finally {
      setLoading(false);
    }
  };

  // Auto-expand the specific cycle if user arrived via a notification deep link
  // (e.g. /documents/<id>/cycles?cycle=<cycle_id>)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cycleParam = params.get('cycle');
    if (cycleParam && cycles.length > 0) {
      const target = cycles.find(c => c.id === cycleParam);
      if (target) {
        setExpandedCycleId(cycleParam);
        // Smooth-scroll to the cycle card so the reviewer sees it immediately
        setTimeout(() => {
          const el = document.querySelector(`[data-cycle-id="${cycleParam}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 200);
      }
    }
    // eslint-disable-next-line
  }, [cycles, location.search]);

  // Fetch DDM suggestions based on document discipline
  const fetchDDMSuggestions = async () => {
    if (!currentDoc?.discipline) {
      toast.info('Set document discipline to get reviewer suggestions');
      return;
    }
    try {
      const params = new URLSearchParams({ discipline: currentDoc.discipline });
      if (currentDoc.company_id) params.append('company_id', currentDoc.company_id);
      if (currentDoc.project_code) params.append('project_code', currentDoc.project_code);
      
      const response = await axios.get(`${API}/ddm/suggest-reviewers?${params}`);
      const suggestions = response.data;
      
      if (suggestions.source) {
        setNewCycle(prev => ({
          ...prev,
          responsible_engineer: suggestions.responsible_engineer?.id || prev.responsible_engineer,
          discipline_reviewer: suggestions.discipline_reviewer?.id || prev.discipline_reviewer,
          project_manager: suggestions.project_manager?.id || prev.project_manager,
          document_controller: suggestions.document_controller?.id || prev.document_controller
        }));
        toast.success(`Reviewers suggested from ${suggestions.source} DDM`);
      } else {
        toast.info('No DDM rule found for this discipline');
      }
    } catch (error) {
      console.error('Failed to fetch DDM suggestions');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const handleCreateCycle = async (e) => {
    e.preventDefault();
    
    if (!newCycle.revision) {
      toast.error('Revision name is required');
      return;
    }

    try {
      // Create the revision
      const response = await axios.post(`${API}/cycles`, {
        document_id: documentId,
        revision: newCycle.revision,
        revision_date: newCycle.revision_date || null,
        classification: newCycle.classification || null,
        responsible_engineer: newCycle.responsible_engineer,
        discipline_reviewer: newCycle.discipline_reviewer,
        project_manager: newCycle.project_manager,
        document_controller: newCycle.document_controller,
        reviewers: newCycle.reviewers, // Multiple reviewers
        incoming_transmittal_nr: newCycle.incoming_transmittal_nr,
        incoming_transmittal_date: newCycle.incoming_transmittal_date,
        incoming_transmittal_id: newCycle.incoming_transmittal_id,
        outgoing_transmittal_nr: newCycle.outgoing_transmittal_nr,
        outgoing_transmittal_date: newCycle.outgoing_transmittal_date,
        outgoing_transmittal_id: newCycle.outgoing_transmittal_id,
        manual_comments: newCycle.manual_comments || null
      });
      
      const cycleId = response.data.id;
      
      // Upload PDF file if provided (direct-to-GCS via signed URL to bypass Cloud Run 32MB limit)
      if (newCycle.pdf_file) {
        const pdfInfo = await uploadFileToGCS(newCycle.pdf_file, 'reviews');
        await axios.post(`${API}/cycles/${cycleId}/register-pdf-gcs`, pdfInfo);
      }
      
      // Upload native file if provided (direct-to-GCS)
      if (newCycle.native_file) {
        const nativeInfo = await uploadFileToGCS(newCycle.native_file, 'reviews');
        await axios.post(`${API}/cycles/${cycleId}/register-native-gcs`, nativeInfo);
      }
      
      toast.success('Revision created successfully. Use "Upload Files" to add documents.');
      setCreateOpen(false);
      setNewCycle({
        revision: '',
        revision_date: '',
        classification: '',
        responsible_engineer: '',
        discipline_reviewer: '',
        project_manager: '',
        document_controller: '',
        reviewers: [],
        incoming_transmittal_nr: '',
        incoming_transmittal_date: '',
        incoming_transmittal_id: '',
        outgoing_transmittal_nr: '',
        outgoing_transmittal_date: '',
        outgoing_transmittal_id: '',
        manual_comments: '',
        pdf_file: null,
        native_file: null
      });
      fetchCycles();
    } catch (error) {
      toast.error('Failed to create revision');
    }
  };

  // Delete revision handler
  const handleDeleteCycle = async (cycleId, revision) => {
    if (!window.confirm(`Are you sure you want to delete revision "${revision}"? This will also delete all associated comments and markups.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/cycles/${cycleId}`);
      toast.success('Revision deleted successfully');
      fetchCycles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete revision');
    }
  };

  const handleUpdateCycleStatus = async (cycleId, newStatus, newStage = null) => {
    try {
      const update = { review_status: newStatus };
      if (newStage) update.review_stage = newStage;
      
      await axios.put(`${API}/cycles/${cycleId}`, update);
      toast.success(`Revision status updated to ${newStatus}`);
      fetchCycles();
    } catch (error) {
      toast.error('Failed to update revision');
    }
  };

  const handleStartReview = async (cycleId) => {
    try {
      await axios.post(`${API}/cycles/${cycleId}/start-review`);
      toast.success('Review started! Reviewers have been notified.');
      fetchCycles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start review');
    }
  };

  const handleApprove = async (cycleId, comments = '') => {
    try {
      const response = await axios.post(`${API}/cycles/${cycleId}/submit-approval`, {
        action: 'approve',
        comments: comments
      });
      toast.success(response.data.message);
      fetchCycles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (cycleId) => {
    const reason = window.prompt('Enter rejection reason:');
    if (reason === null) return; // User cancelled
    
    try {
      await axios.post(`${API}/cycles/${cycleId}/submit-approval`, {
        action: 'reject',
        comments: reason
      });
      toast.success('Revision rejected');
      fetchCycles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  // Edit Reviewers handlers
  const openEditReviewers = (cycle) => {
    setSelectedCycleForEdit(cycle);
    setEditReviewers({
      responsible_engineer: cycle.responsible_engineer || '',
      discipline_reviewer: cycle.discipline_reviewer || '',
      project_manager: cycle.project_manager || '',
      document_controller: cycle.document_controller || ''
    });
    setEditReviewersOpen(true);
  };

  const handleSaveReviewers = async () => {
    if (!selectedCycleForEdit) return;
    
    try {
      await axios.put(`${API}/cycles/${selectedCycleForEdit.id}/reviewers`, editReviewers);
      toast.success('Reviewers updated successfully');
      setEditReviewersOpen(false);
      setSelectedCycleForEdit(null);
      fetchCycles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update reviewers');
    }
  };

  // New workflow handlers
  const openPMReview = async (cycle) => {
    setSelectedCycleForPM(cycle);
    setSelectedReviewCode('');
    setPmComments('');
    // Fetch comments for this cycle
    try {
      const response = await axios.get(`${API}/cycles/${cycle.id}/comments`);
      setCycleComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments');
    }
    setPmReviewOpen(true);
  };

  const handlePMCommentAction = async (commentId, action, modifiedContent = '') => {
    try {
      await axios.put(`${API}/cycles/${selectedCycleForPM.id}/comments/${commentId}/pm-action`, {
        action,
        modified_content: modifiedContent || undefined
      });
      toast.success(`Comment ${action}ed`);
      // Refresh comments
      const response = await axios.get(`${API}/cycles/${selectedCycleForPM.id}/comments`);
      setCycleComments(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} comment`);
    }
  };

  const handleAssignReviewCode = async () => {
    if (!selectedReviewCode) {
      toast.error('Please select a review code');
      return;
    }
    try {
      await axios.post(`${API}/cycles/${selectedCycleForPM.id}/assign-review-code`, {
        review_code: selectedReviewCode,
        comments: pmComments
      });
      toast.success('Review code assigned. Document Controller notified.');
      setPmReviewOpen(false);
      fetchCycles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign review code');
    }
  };

  const openDCConfirm = (cycle) => {
    setSelectedCycleForDC(cycle);
    setDcComments('');
    setDcConfirmOpen(true);
  };

  const handleDCConfirm = async () => {
    try {
      const response = await axios.post(`${API}/cycles/${selectedCycleForDC.id}/dc-confirm`, {
        comments: dcComments
      });
      toast.success(response.data.message);
      setDcConfirmOpen(false);
      fetchCycles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm');
    }
  };

  // Email preview and send functions
  const openEmailPreview = async (cycle) => {
    try {
      const response = await axios.post(`${API}/cycles/${cycle.id}/preview-email`);
      setEmailPreview(response.data);
      setEmailToList(response.data.to_contacts?.map(c => c.email) || []);
      setEmailCcList(response.data.cc_contacts?.map(c => c.email) || []);
      setEmailSubject(response.data.subject || '');
      setSelectedCycleForDC(cycle);
      setEmailDialogOpen(true);
    } catch (error) {
      toast.error('Failed to generate email preview');
    }
  };

  const handleSendEmail = async () => {
    if (emailToList.length === 0) {
      toast.error('At least one TO recipient is required');
      return;
    }
    try {
      const response = await axios.post(`${API}/cycles/${selectedCycleForDC.id}/dc-confirm-send`, {
        to_emails: emailToList,
        cc_emails: emailCcList,
        subject: emailSubject,
        body: emailPreview?.html_preview || '',
        include_pdf: true,
        comments: dcComments
      });
      
      if (response.data.email_result?.preview_mode) {
        toast.success('Email preview saved. SendGrid not configured - add API key to send actual emails.');
      } else if (response.data.email_result?.success) {
        toast.success('Email sent successfully!');
      } else {
        toast.warning(response.data.email_result?.message || 'Email may not have been sent');
      }
      
      setEmailDialogOpen(false);
      setDcConfirmOpen(false);
      fetchCycles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  // Check user permissions
  const canAssignReviewCode = (cycle) => {
    // Responsible Engineer, Admin, or Document Controller can assign review codes
    return cycle.responsible_engineer === user?.id || 
           cycle.project_manager === user?.id || 
           user?.role?.toLowerCase() === 'admin' || 
           user?.role?.toLowerCase() === 'document_controller';
  };

  const canDCConfirm = (cycle) => {
    return cycle.document_controller === user?.id || user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'document_controller';
  };

  // Check if user can download native files
  const canDownloadNative = () => {
    return userAccess?.can_download_native || false;
  };

  // Check if user can upload revisions
  const canUploadRevision = () => {
    return user?.is_super_admin || userAccess?.can_upload_revision || false;
  };

  // Check if user can delete revisions
  const canDeleteRevision = () => {
    return user?.is_super_admin || userAccess?.can_delete_revision || false;
  };

  // Check if user can upload comments (Document Controller and System Admin only)
  const canUploadComments = () => {
    const role = user?.role?.toLowerCase() || '';
    return user?.is_super_admin || role === 'document_controller' || role === 'admin';
  };

  // Check if user can edit revision details (System Admin and Document Controller only)
  const canEditRevision = () => {
    const role = user?.role?.toLowerCase() || '';
    return user?.is_super_admin || role === 'document_controller' || role === 'admin';
  };

  // Open Edit Revision Dialog
  const handleOpenEditRevision = (cycle) => {
    setSelectedCycleForRevisionEdit(cycle);
    setEditRevisionData({
      revision: cycle.revision || '',
      revision_date: cycle.revision_date ? cycle.revision_date.split('T')[0] : '',
      review_status: cycle.review_status || 'open',
      review_stage: cycle.review_stage || 'internal'
    });
    setEditRevisionOpen(true);
  };

  // Handle Edit Revision Submit
  const handleEditRevisionSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCycleForRevisionEdit) return;
    
    try {
      await axios.put(`${API}/cycles/${selectedCycleForRevisionEdit.id}`, editRevisionData);
      toast.success('Revision updated successfully');
      setEditRevisionOpen(false);
      setSelectedCycleForRevisionEdit(null);
      fetchCycles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update revision');
    }
  };

  // File upload handlers for existing revisions
  const handleUploadPdf = async (cycleId, file) => {
    if (!file) return;
    
    setUploadingFile(true);
    try {
      // Direct-to-GCS upload (bypasses Cloud Run 32MB body limit)
      const pdfInfo = await uploadFileToGCS(file, 'reviews');
      await axios.post(`${API}/cycles/${cycleId}/register-pdf-gcs`, pdfInfo);
      
      toast.success('PDF uploaded successfully');
      fetchCycles();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to upload PDF');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleUploadNative = async (cycleId, file) => {
    if (!file) return;
    
    setUploadingFile(true);
    try {
      const nativeInfo = await uploadFileToGCS(file, 'reviews');
      await axios.post(`${API}/cycles/${cycleId}/register-native-gcs`, nativeInfo);
      
      toast.success('Native file uploaded successfully');
      fetchCycles();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to upload native file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDownloadNative = async (cycle) => {
    if (!canDownloadNative()) {
      toast.error('Only Document Controllers and Admins can download native files');
      return;
    }
    try {
      const response = await axios.get(`${API}/cycles/${cycle.id}/download-native`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', cycle.native_file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download native file');
    }
  };

  // Comments file handlers
  const openCommentsUpload = (cycle) => {
    setSelectedCycleForComments(cycle);
    setCommentsUploadOpen(true);
  };

  const handleUploadComments = async (cycleId, file) => {
    if (!file) return;
    
    try {
      setUploadingFile(true);
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/cycles/${cycleId}/upload-comments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Comments file uploaded successfully');
      setCommentsUploadOpen(false);
      setSelectedCycleForComments(null);
      fetchCycles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload comments file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDownloadComments = async (cycle) => {
    try {
      const response = await axios.get(`${API}/cycles/${cycle.id}/download-comments`, {
        responseType: 'blob'
      });
      
      // Check if response is actually an error blob (JSON error response)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Failed to download');
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', cycle.comments_file_name || 'comments');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Clean up
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to download comments file');
    }
  };

  // Check if current user is a reviewer for this cycle
  const isUserReviewer = (cycle) => {
    if (!user) return false;
    // Check legacy fields
    if (cycle.responsible_engineer === user.id || cycle.discipline_reviewer === user.id) return true;
    // Check new reviewers list
    if (cycle.reviewers?.some(r => r.id === user.id)) return true;
    // Admin is always a reviewer
    if (user.role?.toLowerCase() === 'admin') return true;
    return false;
  };

  // Check if current user has already finalized their review
  const hasUserFinalized = (cycle) => {
    if (!user) return false;
    
    // Admin can always add comments/finalize unless all reviewers have finalized
    if (user.role?.toLowerCase() === 'admin') {
      // For admin, check if all assigned reviewers have finalized
      const reFinalized = !cycle.responsible_engineer || cycle.responsible_engineer_finalized;
      const drFinalized = !cycle.discipline_reviewer || cycle.discipline_reviewer_finalized;
      // Check multi-reviewer list
      const allReviewersFinalized = !cycle.reviewers?.length || cycle.reviewers.every(r => r.finalized);
      return reFinalized && drFinalized && allReviewersFinalized;
    }
    
    // Check legacy fields
    if (cycle.responsible_engineer === user.id && cycle.responsible_engineer_finalized) return true;
    if (cycle.discipline_reviewer === user.id && cycle.discipline_reviewer_finalized) return true;
    
    // Check new reviewers list
    const userInList = cycle.reviewers?.find(r => r.id === user.id);
    if (userInList && userInList.finalized) return true;
    
    return false;
  };

  // Handle adding a comment as reviewer
  const handleAddReviewerComment = async (cycleId) => {
    if (!reviewerComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }
    
    try {
      setAddingComment(true);
      await axios.post(`${API}/cycles/${cycleId}/comments`, {
        comment_text: reviewerComment,
        comment_type: 'comment'
      });
      toast.success('Comment added successfully');
      setReviewerComment('');
      fetchCycles(); // Refresh to show updated comments
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  // Handle finalizing review as reviewer
  const handleFinalizeReview = async (cycleId) => {
    try {
      setFinalizingReview(true);
      await axios.post(`${API}/cycles/${cycleId}/finalize-review`, {
        comments: reviewerComment || ''
      });
      toast.success('Review finalized successfully');
      setReviewerComment('');
      fetchCycles(); // Refresh to show updated status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to finalize review');
    } finally {
      setFinalizingReview(false);
    }
  };

  // Toggle expanded cycle
  const toggleExpandCycle = (cycleId) => {
    if (expandedCycleId === cycleId) {
      setExpandedCycleId(null);
      setReviewerComment('');
      setSelectedReviewCode('');
    } else {
      setExpandedCycleId(cycleId);
      setReviewerComment('');
      setSelectedReviewCode('');
    }
  };

  // Quick close - directly assign review code and close document
  const handleQuickClose = async (cycleId) => {
    if (!selectedReviewCode) {
      toast.error('Please select a review code');
      return;
    }
    
    try {
      await axios.post(`${API}/cycles/${cycleId}/quick-close`, {
        review_code: selectedReviewCode,
        comments: reviewerComment || ''
      });
      toast.success('Document closed with review code');
      setSelectedReviewCode('');
      setReviewerComment('');
      setExpandedCycleId(null);
      fetchCycles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close document');
    }
  };

  const getUserName = (userId) => {
    const u = users.find(user => user.id === userId);
    return u ? u.full_name : '-';
  };

  const getStageLabel = (stage) => {
    const labels = {
      'internal': 'Reviewer',
      'pm_review': 'RE Review',
      'dc_confirmation': 'DC Confirmation',
      'sent_to_contractor': 'Sent to Contractor',
      'rejected': 'Rejected'
    };
    return labels[stage] || stage;
  };

  const getStatusBadge = (status) => {
    const colors = {
      'open': 'bg-gray-100 text-gray-800',
      'in_review': 'bg-blue-100 text-blue-800',
      'pending_pm': 'bg-yellow-100 text-yellow-800',
      'pending_dc': 'bg-orange-100 text-orange-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'closed': 'bg-green-100 text-green-800'
    };
    const labels = {
      'open': 'Open',
      'in_review': 'In Review',
      'pending_pm': 'Pending RE',
      'pending_dc': 'Pending DC',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'closed': 'Complete'
    };
    return (
      <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-medium ${colors[status] || colors.open}`}>
        {labels[status] || status}
      </span>
    );
  };

  const canUserApprove = (cycle) => {
    // Check if current user is assigned as a reviewer
    if (cycle.responsible_engineer === user?.id) return true;
    if (cycle.discipline_reviewer === user?.id) return true;
    if (cycle.project_manager === user?.id) return true;
    if (user?.role?.toLowerCase() === 'admin') return true;
    return false;
  };

  const handleDownloadPdf = async (cycle) => {
    try {
      const response = await axios.get(`${API}/cycles/${cycle.id}/download-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', cycle.pdf_file_name || 'document.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Download started');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const openUploadDialog = (cycle) => {
    setSelectedCycleForUpload(cycle);
    setUploadOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in_review': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'internal': return 'bg-purple-100 text-purple-800';
      case 'external': return 'bg-orange-100 text-orange-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Export functions
  const handleExportPDF = async (cycle) => {
    try {
      toast.info('Generating PDF report...');
      const response = await axios.get(`${API}/export/cycles/${cycle.id}/pdf`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `${currentDoc?.document_number || 'review'}_Rev${cycle.revision}_Report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF report downloaded');
    } catch (error) {
      console.error('Export PDF error:', error);
      toast.error('Failed to export PDF report');
    }
  };

  const handleExportExcel = async (cycle) => {
    try {
      toast.info('Generating Excel report...');
      const response = await axios.get(`${API}/export/cycles/${cycle.id}/excel`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `${currentDoc?.document_number || 'review'}_Rev${cycle.revision}_Report.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel report downloaded');
    } catch (error) {
      console.error('Export Excel error:', error);
      toast.error('Failed to export Excel report');
    }
  };

  if (viewCycleId) {
    const currentCycle = cycles.find(c => c.id === viewCycleId);
    return (
      <div className="h-screen flex flex-col">
        <div className="p-3 bg-white border-b border-border flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => setViewCycleId(null)}>
            <ArrowLeft size={14} className="mr-1" />
            Back
          </Button>
          <h2 className="text-sm font-semibold">Revision {currentCycle?.revision || 'Unknown'}</h2>
        </div>
        <div className="flex-1">
          <PDFViewerSVG 
            documentId={documentId} 
            cycleId={viewCycleId} 
            user={user}
            cycleData={currentCycle}
            onRefresh={fetchCycles}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="review-cycles-page">
      {/* Header */}
      <div className="mb-4">
        <Link to="/documents" className="inline-flex items-center gap-1 text-primary hover:underline mb-2 text-sm">
          <ArrowLeft size={16} />
          Back to Documents
        </Link>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Document Revisions
            </h1>
            <p className="text-muted-foreground text-sm" data-testid="revisions-doc-subtitle">
              {currentDoc ? (
                <>
                  {currentDoc.document_number && (
                    <span className="font-semibold text-foreground mr-2" data-testid="revisions-doc-number">
                      {currentDoc.document_number}
                    </span>
                  )}
                  <span>{currentDoc.title || currentDoc.name}</span>
                </>
              ) : 'Loading...'}
            </p>
          </div>
          
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary" size="sm" data-testid="create-cycle-button">
                <Plus size={16} className="mr-1" />
                Create Revision
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="create-cycle-dialog" className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-base">Create New Revision</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateCycle} className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Revision*</Label>
                    <Input
                      value={newCycle.revision}
                      onChange={(e) => setNewCycle({ ...newCycle, revision: e.target.value })}
                      placeholder="e.g., A, B, 01, 02"
                      required
                      className="h-8 text-sm"
                      data-testid="revision-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Revision Date</Label>
                    <Input
                      type="date"
                      value={newCycle.revision_date}
                      onChange={(e) => setNewCycle({ ...newCycle, revision_date: e.target.value })}
                      className="h-8 text-sm"
                      data-testid="revision-date-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Classification</Label>
                    <Input
                      value={newCycle.classification}
                      onChange={(e) => setNewCycle({ ...newCycle, classification: e.target.value })}
                      placeholder="e.g., IFR, IFA, IFC"
                      className="h-8 text-sm"
                      list="revision-classification-options"
                      data-testid="revision-classification-input"
                    />
                    <datalist id="revision-classification-options">
                      {['IFR', 'IFA', 'IFC', 'IFP', 'IFI', 'IFD', 'AB'].map(opt => (
                        <option key={opt} value={opt} />
                      ))}
                    </datalist>
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Upload PDF and native files after creating.
                </p>
                
                {/* DDM Suggestion Button */}
                <div className="bg-blue-50 border border-blue-200 rounded-sm p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-blue-800">Auto-assign Reviewers</p>
                      <p className="text-[10px] text-blue-600">Based on document discipline: {document?.discipline || 'Not set'}</p>
                    </div>
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="outline" 
                      className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                      onClick={fetchDDMSuggestions}
                      disabled={!document?.discipline}
                    >
                      <Lightning size={12} className="mr-1" />
                      Suggest from DDM
                    </Button>
                  </div>
                </div>
                
                {/* Stage 1: Reviewers */}
                <div className="border-t pt-2">
                  <h4 className="text-xs font-semibold mb-2">Stage 1: Reviewers (Select Multiple)</h4>
                  
                  {/* Multi-select reviewers */}
                  <div className="mb-3">
                    <Label className="text-xs mb-1 block">Add Reviewers</Label>
                    <div className="flex gap-2 mb-2">
                      <Select 
                        value="" 
                        onValueChange={(val) => {
                          if (val && !newCycle.reviewers.includes(val)) {
                            setNewCycle({ ...newCycle, reviewers: [...newCycle.reviewers, val] });
                          }
                        }}
                      >
                        <SelectTrigger className="h-8 text-sm flex-1">
                          <SelectValue placeholder="Select reviewer to add" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.filter(u => !newCycle.reviewers.includes(u.id)).map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.role})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Selected reviewers */}
                    {newCycle.reviewers.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {newCycle.reviewers.map(reviewerId => {
                          const reviewer = users.find(u => u.id === reviewerId);
                          return (
                            <span 
                              key={reviewerId}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded"
                            >
                              {reviewer?.full_name || 'Unknown'}
                              <button
                                type="button"
                                onClick={() => setNewCycle({ 
                                  ...newCycle, 
                                  reviewers: newCycle.reviewers.filter(id => id !== reviewerId) 
                                })}
                                className="hover:text-blue-900"
                              >
                                <X size={12} />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {newCycle.reviewers.length === 0 && (
                      <p className="text-xs text-muted-foreground">No reviewers selected</p>
                    )}
                  </div>
                </div>
                
                {/* Stage 2: Review Code Assignment */}
                <div className="border-t pt-2">
                  <h4 className="text-xs font-semibold mb-2">Stage 2: Review Code Assignment (RE/PM)</h4>
                  <div>
                    <Label className="text-xs">Project Manager (Optional)</Label>
                    <Select value={newCycle.project_manager} onValueChange={(val) => setNewCycle({ ...newCycle, project_manager: val })}>
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Stage 3: Document Controller */}
                <div className="border-t pt-2">
                  <h4 className="text-xs font-semibold mb-2">Stage 3: Document Controller</h4>
                  <div>
                    <Label className="text-xs">Document Controller</Label>
                    <Select value={newCycle.document_controller} onValueChange={(val) => setNewCycle({ ...newCycle, document_controller: val })}>
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Transmittal Information */}
                <div className="border-t pt-2">
                  <h4 className="text-xs font-semibold mb-2 flex items-center gap-1">
                    <PaperPlaneTilt size={12} />
                    Transmittal
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Incoming Transmittal</Label>
                      <Select value={newCycle.incoming_transmittal_id} onValueChange={(val) => {
                        const t = transmittals.find(tr => tr.id === val);
                        setNewCycle({ 
                          ...newCycle, 
                          incoming_transmittal_id: val,
                          incoming_transmittal_nr: t?.transmittal_nr || '',
                          incoming_transmittal_date: t?.date_received?.split('T')[0] || ''
                        });
                      }}>
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {transmittals.filter(t => t.transmittal_type === 'incoming').map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.transmittal_nr}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Incoming Date</Label>
                      <Input
                        type="date"
                        className="h-8 text-sm"
                        value={newCycle.incoming_transmittal_date}
                        onChange={(e) => setNewCycle({ ...newCycle, incoming_transmittal_date: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Outgoing Transmittal</Label>
                      <Select value={newCycle.outgoing_transmittal_id} onValueChange={(val) => {
                        const t = transmittals.find(tr => tr.id === val);
                        setNewCycle({ 
                          ...newCycle, 
                          outgoing_transmittal_id: val,
                          outgoing_transmittal_nr: t?.transmittal_nr || '',
                          outgoing_transmittal_date: t?.sent_date?.split('T')[0] || ''
                        });
                      }}>
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {transmittals.filter(t => t.transmittal_type === 'outgoing').map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.transmittal_nr}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Outgoing Date</Label>
                      <Input
                        type="date"
                        className="h-8 text-sm"
                        value={newCycle.outgoing_transmittal_date}
                        onChange={(e) => setNewCycle({ ...newCycle, outgoing_transmittal_date: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Manual Comments Section */}
                <div className="border-t pt-2">
                  <h4 className="text-xs font-semibold mb-2">Comments (External Review)</h4>
                  <div>
                    <Label className="text-xs">Manual Comments</Label>
                    <Textarea
                      value={newCycle.manual_comments}
                      onChange={(e) => setNewCycle({ ...newCycle, manual_comments: e.target.value })}
                      placeholder="Add comments from external review (if document was reviewed outside DocStat)"
                      className="h-20 text-sm"
                      data-testid="manual-comments-input"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Use this field to record review feedback when the document was reviewed outside of DocStat.
                    </p>
                  </div>
                </div>
                
                <Button type="submit" className="w-full bg-primary" size="sm">
                  Create Revision
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Revisions list */}
      {loading ? (
        <div className="text-center py-8 text-sm">Loading...</div>
      ) : cycles.length === 0 ? (
        <div className="text-center py-8 border border-border rounded-sm bg-white">
          <FileText size={36} className="mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No revisions yet. Create one to start the review process.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {cycles.map((cycle) => (
            <ContextMenu key={cycle.id}>
              <ContextMenuTrigger>
                {/* iOS-Style Compact Card */}
                <div 
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all duration-200 cursor-context-menu" 
                  data-testid={`cycle-${cycle.id}`}
                  data-cycle-id={cycle.id}
                >
                  {/* Main Row */}
                  <div className="px-4 py-3 flex items-center gap-4">
                    {/* Revision Badge */}
                    <div className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${
                      cycle.review_status === 'approved' ? 'bg-green-500' :
                      cycle.review_status === 'in_review' ? 'bg-blue-500' :
                      cycle.review_status === 'rejected' ? 'bg-red-500' :
                      'bg-gray-400'
                    }`}>
                      <span className="text-white text-lg font-bold">{cycle.revision}</span>
                      <span className="text-white/80 text-[8px] uppercase tracking-wider">Rev</span>
                    </div>

                    {/* Info Section */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          cycle.review_status === 'approved' ? 'bg-green-100 text-green-700' :
                          cycle.review_status === 'in_review' ? 'bg-blue-100 text-blue-700' :
                          cycle.review_status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {cycle.review_status === 'in_review' ? 'In Review' : 
                           cycle.review_status?.charAt(0).toUpperCase() + cycle.review_status?.slice(1)}
                        </span>
                        {cycle.review_code_label && (
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${REVIEW_CODES[cycle.review_code]?.color || 'bg-gray-100'}`}>
                            {cycle.review_code_label}
                          </span>
                        )}
                        {/* v27b: transmittal badges on revision cards */}
                        {(() => {
                          const tList = Array.isArray(transmittals) ? transmittals : [];
                          const matches = tList.filter((t) => {
                            if (Array.isArray(t.linked_revision_ids) && t.linked_revision_ids.includes(cycle.id)) return true;
                            if (Array.isArray(t.documents)) {
                              return t.documents.some(
                                (td) => td?.document_id === documentId && (td?.revision || '') === (cycle.revision || '')
                              );
                            }
                            return false;
                          });
                          const seen = new Set();
                          const unique = matches.filter((t) => {
                            const k = `${t.transmittal_type}-${t.transmittal_nr}`;
                            if (seen.has(k)) return false;
                            seen.add(k);
                            return true;
                          });
                          if (unique.length === 0 && !cycle.outgoing_transmittal_nr && !cycle.incoming_transmittal_nr) return null;
                          const incoming = unique.filter((t) => t.transmittal_type === 'incoming');
                          const outgoing = unique.filter((t) => t.transmittal_type === 'outgoing');
                          if (incoming.length === 0 && cycle.incoming_transmittal_nr) {
                            incoming.push({ transmittal_nr: cycle.incoming_transmittal_nr });
                          }
                          if (outgoing.length === 0 && cycle.outgoing_transmittal_nr) {
                            outgoing.push({ transmittal_nr: cycle.outgoing_transmittal_nr });
                          }
                          return (
                            <>
                              {incoming.map((t, i) => (
                                <span
                                  key={`tin-${cycle.id}-${i}`}
                                  className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200"
                                  data-testid={`incoming-transmittal-${cycle.id}`}
                                  title={`Incoming transmittal: ${t.transmittal_nr}`}
                                >
                                  <ArrowSquareIn size={10} />
                                  IN: {t.transmittal_nr}
                                </span>
                              ))}
                              {outgoing.map((t, i) => (
                                <span
                                  key={`tout-${cycle.id}-${i}`}
                                  className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200"
                                  data-testid={`outgoing-transmittal-${cycle.id}`}
                                  title={`Outgoing transmittal: ${t.transmittal_nr}`}
                                >
                                  <ArrowSquareOut size={10} />
                                  OUT: {t.transmittal_nr}
                                </span>
                              ))}
                            </>
                          );
                        })()}
                      </div>

                      {/* Progress Bar - iOS Style */}
                      <div className="flex items-center gap-1 mb-1.5">
                        <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden flex gap-0.5">
                          <div className={`h-full rounded-full transition-all ${
                            ['pm_review', 'dc_confirmation', 'sent_to_contractor'].includes(cycle.review_stage) ? 'bg-green-500 w-1/4' :
                            cycle.review_stage === 'internal' && cycle.review_status === 'in_review' ? 'bg-blue-500 w-1/4' :
                            cycle.review_stage === 'rejected' ? 'bg-red-500 w-1/4' : 'bg-gray-300 w-0'
                          }`}></div>
                          <div className={`h-full rounded-full transition-all ${
                            ['dc_confirmation', 'sent_to_contractor'].includes(cycle.review_stage) ? 'bg-green-500 w-1/4' :
                            cycle.review_stage === 'pm_review' ? 'bg-blue-500 w-1/4' : 'bg-transparent w-0'
                          }`}></div>
                          <div className={`h-full rounded-full transition-all ${
                            cycle.review_stage === 'sent_to_contractor' ? 'bg-green-500 w-1/4' :
                            cycle.review_stage === 'dc_confirmation' ? 'bg-blue-500 w-1/4' : 'bg-transparent w-0'
                          }`}></div>
                          <div className={`h-full rounded-full transition-all ${
                            cycle.review_stage === 'sent_to_contractor' ? 'bg-green-500 w-1/4' : 'bg-transparent w-0'
                          }`}></div>
                        </div>
                        <span className="text-[9px] text-gray-400 w-16 text-right">
                          {cycle.review_stage === 'internal' ? 'Review' :
                           cycle.review_stage === 'pm_review' ? 'RE' :
                           cycle.review_stage === 'dc_confirmation' ? 'DC' :
                           cycle.review_stage === 'sent_to_contractor' ? 'Sent' : 'Draft'}
                        </span>
                      </div>

                      {/* Expanded File Section with Download Buttons */}
                      <div className="flex items-center gap-2 mt-2">
                        {/* PDF File */}
                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl ${
                          cycle.pdf_file_name ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'
                        }`}>
                          <FileText size={16} className={cycle.pdf_file_name ? 'text-red-500' : 'text-gray-300'} />
                          <div className="flex flex-col">
                            <span className={`text-[10px] font-medium ${cycle.pdf_file_name ? 'text-red-700' : 'text-gray-400'}`}>
                              PDF
                            </span>
                            {cycle.pdf_file_name ? (
                              <span className="text-[9px] text-red-500 truncate max-w-[80px]" title={`${cycle.pdf_file_name} (${formatFileSize(cycle.pdf_file_size)})`}>
                                {cycle.pdf_file_name.substring(0, 12)}...
                              </span>
                            ) : (
                              <span className="text-[9px] text-gray-400">Not uploaded</span>
                            )}
                            {cycle.pdf_file_name && (
                              <span className="text-[9px] font-medium text-red-600" data-testid={`rev-pdf-size-${cycle.revision}`}>
                                {formatFileSize(cycle.pdf_file_size)}
                              </span>
                            )}
                          </div>
                          {cycle.pdf_file_name && (
                            <button 
                              onClick={(e) => { e.stopPropagation(); handleDownloadPdf(cycle); }}
                              className="ml-1 p-1 rounded-full hover:bg-red-100 transition-colors"
                              title="Download PDF"
                            >
                              <Download size={14} className="text-red-600" />
                            </button>
                          )}
                        </div>

                        {/* Native File */}
                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl ${
                          cycle.native_file_name ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'
                        }`}>
                          <Archive size={16} className={cycle.native_file_name ? 'text-blue-500' : 'text-gray-300'} />
                          <div className="flex flex-col">
                            <span className={`text-[10px] font-medium ${cycle.native_file_name ? 'text-blue-700' : 'text-gray-400'}`}>
                              Native
                            </span>
                            {cycle.native_file_name ? (
                              <span className="text-[9px] text-blue-500 truncate max-w-[80px]" title={`${cycle.native_file_name} (${formatFileSize(cycle.native_file_size)})`}>
                                {cycle.native_file_name.substring(0, 12)}...
                              </span>
                            ) : (
                              <span className="text-[9px] text-gray-400">Not uploaded</span>
                            )}
                            {cycle.native_file_name && (
                              <span className="text-[9px] font-medium text-blue-600" data-testid={`rev-native-size-${cycle.revision}`}>
                                {formatFileSize(cycle.native_file_size)}
                              </span>
                            )}
                          </div>
                          {cycle.native_file_name && (
                            canDownloadNative() ? (
                              <button 
                                onClick={(e) => { e.stopPropagation(); handleDownloadNative(cycle); }}
                                className="ml-1 p-1 rounded-full hover:bg-blue-100 transition-colors"
                                title="Download Native File"
                              >
                                <Download size={14} className="text-blue-600" />
                              </button>
                            ) : (
                              <div className="ml-1 p-1" title="Permission required">
                                <Lock size={14} className="text-gray-400" />
                              </div>
                            )
                          )}
                        </div>

                        {/* Comments File */}
                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl ${
                          cycle.comments_file_name ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
                        }`}>
                          <ChatCircle size={16} className={cycle.comments_file_name ? 'text-green-500' : 'text-gray-300'} />
                          <div className="flex flex-col">
                            <span className={`text-[10px] font-medium ${cycle.comments_file_name ? 'text-green-700' : 'text-gray-400'}`}>
                              Comments
                            </span>
                            {cycle.comments_file_name ? (
                              <span className="text-[9px] text-green-500 truncate max-w-[80px]" title={cycle.comments_file_name}>
                                {cycle.comments_file_name.substring(0, 12)}...
                              </span>
                            ) : (
                              <span className="text-[9px] text-gray-400">Not uploaded</span>
                            )}
                          </div>
                          <div className="flex items-center">
                            {cycle.comments_file_name && (
                              <button 
                                onClick={(e) => { e.stopPropagation(); handleDownloadComments(cycle); }}
                                className="ml-1 p-1 rounded-full hover:bg-green-100 transition-colors"
                                title="Download Comments"
                              >
                                <Download size={14} className="text-green-600" />
                              </button>
                            )}
                            {canUploadComments() && (
                              <button 
                                onClick={(e) => { e.stopPropagation(); openCommentsUpload(cycle); }}
                                className="p-1 rounded-full hover:bg-green-100 transition-colors"
                                title="Upload Comments (Document Controller only)"
                              >
                                <UploadSimple size={14} className="text-green-600" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Date */}
                        <span className="text-[10px] text-gray-400 ml-auto">
                          {new Date(cycle.created_at ? (String(cycle.created_at).endsWith('Z') || String(cycle.created_at).includes('+') ? cycle.created_at : cycle.created_at + 'Z') : 0).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {/* Reviewers Avatars - Clickable to expand */}
                    <div 
                      className="flex -space-x-2 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => toggleExpandCycle(cycle.id)}
                      title="Click to expand reviewer actions"
                    >
                      {/* Legacy single reviewers */}
                      {cycle.responsible_engineer && (
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold border-2 border-white ${
                          cycle.responsible_engineer_finalized ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
                        }`} title={`Lead: ${getUserName(cycle.responsible_engineer)} ${cycle.responsible_engineer_finalized ? '✓' : ''}`}>
                          {getUserName(cycle.responsible_engineer)?.charAt(0) || 'L'}
                        </div>
                      )}
                      {cycle.discipline_reviewer && (
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold border-2 border-white ${
                          cycle.discipline_reviewer_finalized ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
                        }`} title={`Disc: ${getUserName(cycle.discipline_reviewer)} ${cycle.discipline_reviewer_finalized ? '✓' : ''}`}>
                          {getUserName(cycle.discipline_reviewer)?.charAt(0) || 'D'}
                        </div>
                      )}
                      
                      {/* Multiple reviewers from list */}
                      {cycle.reviewers?.map((reviewer, idx) => (
                        <div 
                          key={reviewer.id || idx}
                          className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold border-2 border-white ${
                            reviewer.finalized ? 'bg-green-500 text-white' : 'bg-blue-200 text-blue-700'
                          }`} 
                          title={`${reviewer.name || 'Reviewer'} (${reviewer.role || 'Reviewer'}) ${reviewer.finalized ? '✓' : ''}`}
                        >
                          {(reviewer.name || 'R').charAt(0)}
                        </div>
                      ))}
                      
                      {cycle.project_manager && (
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold border-2 border-white ${
                          cycle.pm_review_completed ? 'bg-green-500 text-white' : 'bg-yellow-200 text-yellow-700'
                        }`} title={`PM: ${getUserName(cycle.project_manager)} ${cycle.pm_review_completed ? '✓' : ''}`}>
                          {getUserName(cycle.project_manager)?.charAt(0) || 'P'}
                        </div>
                      )}
                      {/* Expand indicator */}
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] border-2 border-white bg-gray-100 text-gray-500 transition-transform ${expandedCycleId === cycle.id ? 'rotate-180' : ''}`}>
                        <CaretDown size={12} />
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {cycle.review_status === 'open' && (
                        <Button 
                          size="sm" 
                          className="h-8 px-3 rounded-full bg-blue-500 hover:bg-blue-600 text-xs font-medium"
                          onClick={() => handleStartReview(cycle.id)}
                        >
                          Start
                        </Button>
                      )}
                      {cycle.review_stage === 'pm_review' && canAssignReviewCode(cycle) && (
                        <Button 
                          size="sm" 
                          className="h-8 px-3 rounded-full bg-yellow-500 hover:bg-yellow-600 text-xs font-medium"
                          onClick={() => openPMReview(cycle)}
                        >
                          PM
                        </Button>
                      )}
                      {cycle.review_stage === 'dc_confirmation' && canDCConfirm(cycle) && (
                        <Button 
                          size="sm" 
                          className="h-8 px-3 rounded-full bg-orange-500 hover:bg-orange-600 text-xs font-medium"
                          onClick={() => openDCConfirm(cycle)}
                        >
                          DC
                        </Button>
                      )}
                      {canUploadRevision() && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="h-8 w-8 p-0 rounded-full hover:bg-gray-100"
                          onClick={() => openUploadDialog(cycle)}
                          data-testid={`upload-files-${cycle.id}`}
                        >
                          <UploadSimple size={16} className="text-gray-500" />
                        </Button>
                      )}
                      {canEditRevision() && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="h-8 w-8 p-0 rounded-full hover:bg-gray-100"
                          onClick={() => handleOpenEditRevision(cycle)}
                          title="Edit Revision Details"
                        >
                          <PencilSimple size={16} className="text-gray-500" />
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        className="h-8 px-4 rounded-full bg-gray-900 hover:bg-gray-800 text-xs font-medium"
                        onClick={() => setViewCycleId(cycle.id)} 
                        disabled={!cycle.pdf_file_name}
                        data-testid={`view-cycle-${cycle.id}`}
                      >
                        <Eye size={14} className="mr-1" />
                        Review
                      </Button>
                    </div>
                  </div>

                  {/* Expandable Details - Transmittal Info */}
                  {(cycle.auto_transmittal_nr || cycle.incoming_transmittal_nr || cycle.outgoing_transmittal_nr) && (
                    <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex items-center gap-4 text-[10px]">
                      {cycle.incoming_transmittal_nr && (
                        <span className="text-blue-600">
                          <ArrowDown size={10} className="inline mr-1" />
                          In: {cycle.incoming_transmittal_nr}
                        </span>
                      )}
                      {cycle.outgoing_transmittal_nr && (
                        <span className="text-green-600">
                          <ArrowUp size={10} className="inline mr-1" />
                          Out: {cycle.outgoing_transmittal_nr}
                        </span>
                      )}
                      {cycle.auto_transmittal_nr && (
                        <span className="text-orange-600">
                          <Truck size={10} className="inline mr-1" />
                          Auto: {cycle.auto_transmittal_nr}
                        </span>
                      )}
                      {cycle.review_stage === 'sent_to_contractor' && (
                        <span className="ml-auto text-green-600 font-medium">
                          <Check size={10} className="inline mr-1" />
                          Sent to Contractor
                        </span>
                      )}
                    </div>
                  )}

                  {/* Rejection Banner */}
                  {cycle.review_stage === 'rejected' && cycle.rejection_reason && (
                    <div className="px-4 py-2 bg-red-50 border-t border-red-100 text-[11px] text-red-700">
                      <strong>Rejected:</strong> {cycle.rejection_reason}
                    </div>
                  )}

                  {/* Expandable Reviewer Actions Panel */}
                  {expandedCycleId === cycle.id && (
                    <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                      {/* Quick Actions */}
                      <div className="mb-3">
                        <h4 className="text-xs font-semibold mb-2 text-gray-700">Quick Actions</h4>
                        <div className="flex flex-wrap gap-2">
                          {/* Review Button */}
                          {cycle.pdf_file_name && (
                            <Button
                              size="sm"
                              className="h-7 px-3 text-xs bg-blue-600 hover:bg-blue-700"
                              onClick={() => setViewCycleId(cycle.id)}
                            >
                              <Eye size={12} className="mr-1" />
                              Review PDF
                            </Button>
                          )}
                          {/* Upload Comments - Document Controller and System Admin only */}
                          {canUploadComments() && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-3 text-xs"
                              onClick={() => openCommentsUpload(cycle)}
                            >
                              <UploadSimple size={12} className="mr-1" />
                              Upload Comments
                            </Button>
                          )}
                          {/* Download Comments */}
                          {cycle.comments_file_name && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-3 text-xs text-green-600 border-green-300 hover:bg-green-50"
                              onClick={() => handleDownloadComments(cycle)}
                            >
                              <Download size={12} className="mr-1" />
                              Download Comments
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Add Comment Section */}
                      <div className="border-t border-gray-200 pt-3 mb-3">
                        <h4 className="text-xs font-semibold mb-2 text-gray-700">Add Review Comment</h4>
                        <Textarea
                          value={reviewerComment}
                          onChange={(e) => setReviewerComment(e.target.value)}
                          placeholder="Enter your review comments..."
                          className="text-xs h-16 mb-2"
                          data-testid={`reviewer-comment-${cycle.id}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-3 text-xs"
                          onClick={() => handleAddReviewerComment(cycle.id)}
                          disabled={addingComment || !reviewerComment.trim()}
                        >
                          {addingComment ? 'Adding...' : 'Add Comment'}
                        </Button>
                      </div>

                      {/* Assign Review Code & Close */}
                      {cycle.review_status !== 'approved' && cycle.review_status !== 'closed' && (
                        <div className="border-t border-gray-200 pt-3">
                          <h4 className="text-xs font-semibold mb-2 text-gray-700">Assign Review Code & Close</h4>
                          <div className="grid grid-cols-2 gap-1.5 mb-2">
                            {Object.entries(REVIEW_CODES).map(([code, { label, color }]) => (
                              <button
                                key={code}
                                onClick={() => setSelectedReviewCode(code)}
                                className={`p-1.5 text-[10px] rounded border text-left ${selectedReviewCode === code ? 'ring-2 ring-primary' : ''} ${color}`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                          <Button
                            size="sm"
                            className="w-full h-7 text-xs bg-green-600 hover:bg-green-700"
                            onClick={() => handleQuickClose(cycle.id)}
                            disabled={!selectedReviewCode}
                          >
                            <Check size={12} className="mr-1" />
                            Close with Review Code
                          </Button>
                        </div>
                      )}

                      {/* Already Closed */}
                      {(cycle.review_status === 'approved' || cycle.review_status === 'closed') && (
                        <div className="border-t border-gray-200 pt-3">
                          <p className="text-xs text-green-600 flex items-center gap-1">
                            <Check size={14} />
                            Document closed with code: {REVIEW_CODES[cycle.review_code]?.label || cycle.review_code}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </ContextMenuTrigger>
              <ContextMenuContent className="w-56">
                <ContextMenuItem onClick={() => openEditReviewers(cycle)} className="cursor-pointer">
                  <PencilSimple size={14} className="mr-2" />
                  Edit Reviewers
                </ContextMenuItem>
                <ContextMenuSeparator />
                {canUploadRevision() && (
                <ContextMenuItem onClick={() => openUploadDialog(cycle)} className="cursor-pointer">
                  <UploadSimple size={14} className="mr-2" />
                  Upload Files
                </ContextMenuItem>
                )}
                {cycle.pdf_file_name && (
                  <ContextMenuItem onClick={() => setViewCycleId(cycle.id)} className="cursor-pointer">
                    <Eye size={14} className="mr-2" />
                    Review Document
                  </ContextMenuItem>
                )}
                {cycle.pdf_file_name && (
                  <ContextMenuItem onClick={() => handleDownloadPdf(cycle)} className="cursor-pointer">
                    <Download size={14} className="mr-2" />
                    Download PDF
                  </ContextMenuItem>
                )}
                <ContextMenuSeparator />
                <ContextMenuItem onClick={() => handleExportPDF(cycle)} className="cursor-pointer">
                  <FilePdf size={14} className="mr-2 text-red-600" />
                  Export Report (PDF)
                </ContextMenuItem>
                <ContextMenuItem onClick={() => handleExportExcel(cycle)} className="cursor-pointer">
                  <FileXls size={14} className="mr-2 text-green-600" />
                  Export Report (Excel)
                </ContextMenuItem>
                {canDeleteRevision() && (
                <>
                <ContextMenuSeparator />
                <ContextMenuItem 
                  onClick={() => handleDeleteCycle(cycle.id, cycle.revision)} 
                  className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                >
                  <X size={14} className="mr-2" />
                  Delete Revision
                </ContextMenuItem>
                </>
                )}
              </ContextMenuContent>
            </ContextMenu>
          ))}
        </div>
      )}
      
      {/* Upload Files Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Upload Files - Revision {selectedCycleForUpload?.revision}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">PDF File (for review/markup)</Label>
              <Input
                ref={pdfInputRef}
                type="file"
                accept=".pdf,.nwd" /* v19 */
                className="h-8 text-sm"
                onChange={(e) => {
                  if (e.target.files?.[0] && selectedCycleForUpload) {
                    handleUploadPdf(selectedCycleForUpload.id, e.target.files[0]);
                  }
                }}
                disabled={uploadingFile}
              />
              {selectedCycleForUpload?.pdf_file_name && (
                <p className="text-[10px] text-green-600 mt-0.5">Current: {selectedCycleForUpload.pdf_file_name}</p>
              )}
            </div>
            <div>
              <Label className="text-xs">Native File (CAD, Word, etc.)</Label>
              <Input
                ref={nativeInputRef}
                type="file"
                className="h-8 text-sm"
                onChange={(e) => {
                  if (e.target.files?.[0] && selectedCycleForUpload) {
                    handleUploadNative(selectedCycleForUpload.id, e.target.files[0]);
                  }
                }}
                disabled={uploadingFile}
              />
              {selectedCycleForUpload?.native_file_name && (
                <p className="text-[10px] text-green-600 mt-0.5">Current: {selectedCycleForUpload.native_file_name}</p>
              )}
            </div>
            {uploadingFile && <p className="text-xs text-blue-600">Uploading...</p>}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Comments Upload Dialog */}
      <Dialog open={commentsUploadOpen} onOpenChange={setCommentsUploadOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <ClipboardText size={16} className="text-green-600" />
              Upload Comments - Revision {selectedCycleForComments?.revision}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-green-50 border border-green-200 rounded-sm">
              <p className="text-xs text-green-800">
                Upload review comments or feedback files from external review processes.
                Supported formats: PDF, Word, Excel, ZIP, etc.
              </p>
            </div>
            <div>
              <Label className="text-xs font-medium">Comments File</Label>
              <Input
                type="file"
                ref={commentsInputRef}
                className="text-sm h-9 cursor-pointer"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.txt,.nwd" /* v19 */
                onChange={(e) => {
                  if (e.target.files?.[0] && selectedCycleForComments) {
                    handleUploadComments(selectedCycleForComments.id, e.target.files[0]);
                  }
                }}
                disabled={uploadingFile}
              />
              {selectedCycleForComments?.comments_file_name && (
                <p className="text-[10px] text-green-600 mt-0.5">Current: {selectedCycleForComments.comments_file_name}</p>
              )}
            </div>
            {uploadingFile && <p className="text-xs text-blue-600">Uploading...</p>}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* PM Review Dialog */}
      <Dialog open={pmReviewOpen} onOpenChange={setPmReviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <ClipboardText size={16} />
              PM Review - Revision {selectedCycleForPM?.revision}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Comments from Reviewers */}
            <div>
              <h4 className="text-sm font-semibold mb-2">Reviewer Comments</h4>
              {cycleComments.length === 0 ? (
                <p className="text-xs text-muted-foreground">No comments yet.</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {cycleComments.map((comment) => (
                    <div key={comment.id} className={`border rounded-sm p-2 text-xs ${
                      comment.pm_action === 'accept' ? 'bg-green-50 border-green-200' :
                      comment.pm_action === 'reject' ? 'bg-red-50 border-red-200' :
                      comment.pm_action === 'modify' ? 'bg-blue-50 border-blue-200' :
                      'bg-white'
                    }`}>
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-medium">{comment.author_name}</span>
                        <span className="text-muted-foreground">{new Date(comment.created_at ? (String(comment.created_at).endsWith('Z') || String(comment.created_at).includes('+') ? comment.created_at : comment.created_at + 'Z') : 0).toLocaleString()}</span>
                      </div>
                      <p className="mb-2">{comment.content}</p>
                      {comment.pm_modified_content && (
                        <p className="text-blue-700 mb-2">PM Modified: {comment.pm_modified_content}</p>
                      )}
                      {!comment.pm_action && (
                        <div className="flex gap-1 mt-2">
                          <Button size="sm" className="h-6 text-[10px] px-2 bg-green-600" onClick={() => handlePMCommentAction(comment.id, 'accept')}>
                            <Check size={10} className="mr-1" />Accept
                          </Button>
                          <Button size="sm" className="h-6 text-[10px] px-2 bg-red-600" onClick={() => handlePMCommentAction(comment.id, 'reject')}>
                            <X size={10} className="mr-1" />Reject
                          </Button>
                          <Button size="sm" className="h-6 text-[10px] px-2" variant="outline" onClick={() => {
                            const modified = window.prompt('Enter modified comment:', comment.content);
                            if (modified) handlePMCommentAction(comment.id, 'modify', modified);
                          }}>
                            Modify
                          </Button>
                        </div>
                      )}
                      {comment.pm_action && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          comment.pm_action === 'accept' ? 'bg-green-200 text-green-800' :
                          comment.pm_action === 'reject' ? 'bg-red-200 text-red-800' :
                          'bg-blue-200 text-blue-800'
                        }`}>
                          {comment.pm_action.charAt(0).toUpperCase() + comment.pm_action.slice(1)}ed
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Review Code Selection */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold mb-2">Assign Review Code</h4>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {Object.entries(REVIEW_CODES).map(([code, { label, color }]) => (
                  <button
                    key={code}
                    onClick={() => setSelectedReviewCode(code)}
                    className={`p-2 text-xs rounded border ${selectedReviewCode === code ? 'ring-2 ring-primary' : ''} ${color}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              
              <div className="mb-3">
                <Label className="text-xs">PM Comments (Optional)</Label>
                <Textarea
                  value={pmComments}
                  onChange={(e) => setPmComments(e.target.value)}
                  placeholder="Add any comments..."
                  rows={2}
                  className="text-sm"
                />
              </div>
              
              <Button onClick={handleAssignReviewCode} className="w-full" size="sm" disabled={!selectedReviewCode}>
                Assign Review Code & Notify DC
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* DC Confirmation Dialog */}
      <Dialog open={dcConfirmOpen} onOpenChange={setDcConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Truck size={16} />
              DC Confirmation - Revision {selectedCycleForDC?.revision}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-green-50 p-3 rounded-sm">
              <p className="text-sm font-medium text-green-800">Review Code: {selectedCycleForDC?.review_code_label}</p>
              {selectedCycleForDC?.pm_comments && (
                <p className="text-xs text-green-700 mt-1">PM Comments: {selectedCycleForDC.pm_comments}</p>
              )}
            </div>
            
            <p className="text-sm text-muted-foreground">
              Choose an action to complete the review process:
            </p>
            
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={handleDCConfirm} variant="outline" size="sm">
                <CheckCircle size={14} className="mr-1" />
                Quick Confirm
              </Button>
              <Button onClick={() => openEmailPreview(selectedCycleForDC)} className="bg-blue-600 hover:bg-blue-700" size="sm">
                <EnvelopeSimple size={14} className="mr-1" />
                Preview & Send Email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Preview & Send Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <EnvelopeSimple size={16} />
              Email Preview - Transmittal {emailPreview?.transmittal_nr}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* SendGrid Status */}
            {emailPreview && !emailPreview.sendgrid_configured && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-2">
                <p className="text-xs text-yellow-800">
                  <strong>Note:</strong> SendGrid not configured. Email will be saved but not sent. Add SENDGRID_API_KEY to enable sending.
                </p>
              </div>
            )}
            
            {/* Email Recipients */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">TO Recipients</Label>
                <div className="space-y-1 mt-1">
                  {emailToList.map((email, idx) => (
                    <div key={idx} className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded text-xs">
                      <span>{email}</span>
                      <button onClick={() => setEmailToList(emailToList.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700">
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                  <Input
                    placeholder="Add email..."
                    className="h-7 text-xs"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value) {
                        setEmailToList([...emailToList, e.target.value]);
                        e.target.value = '';
                        e.preventDefault();
                      }
                    }}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">CC Recipients</Label>
                <div className="space-y-1 mt-1">
                  {emailCcList.map((email, idx) => (
                    <div key={idx} className="flex items-center gap-1 bg-yellow-50 px-2 py-1 rounded text-xs">
                      <span>{email}</span>
                      <button onClick={() => setEmailCcList(emailCcList.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700">
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                  <Input
                    placeholder="Add CC email..."
                    className="h-7 text-xs"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value) {
                        setEmailCcList([...emailCcList, e.target.value]);
                        e.target.value = '';
                        e.preventDefault();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            
            {/* Subject */}
            <div>
              <Label className="text-xs">Subject</Label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            
            {/* DC Comments */}
            <div>
              <Label className="text-xs">DC Comments (Optional)</Label>
              <Textarea
                value={dcComments}
                onChange={(e) => setDcComments(e.target.value)}
                placeholder="Add any comments to include in the transmittal..."
                rows={2}
                className="text-sm"
              />
            </div>
            
            {/* Email Preview */}
            <div>
              <Label className="text-xs">Email Preview</Label>
              <div className="border rounded-sm p-2 mt-1 bg-white max-h-60 overflow-y-auto">
                <div dangerouslySetInnerHTML={{ __html: emailPreview?.html_preview || '' }} />
              </div>
            </div>
            
            {/* Attachment Info */}
            {emailPreview?.has_pdf && (
              <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 p-2 rounded-sm">
                <FileText size={14} />
                PDF will be attached: {emailPreview?.document?.name}_Rev{emailPreview?.document?.revision}.pdf
              </div>
            )}
            
            {/* Send Button */}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEmailDialogOpen(false)} className="flex-1" size="sm">
                Cancel
              </Button>
              <Button onClick={handleSendEmail} className="flex-1 bg-green-600 hover:bg-green-700" size="sm">
                <EnvelopeSimple size={14} className="mr-1" />
                {emailPreview?.sendgrid_configured ? 'Confirm & Send Email' : 'Confirm & Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Reviewers Dialog */}
      <Dialog open={editReviewersOpen} onOpenChange={setEditReviewersOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <UserCircle size={18} />
              Edit Reviewers - Revision {selectedCycleForEdit?.revision}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground bg-blue-50 p-2 rounded-sm">
              Assign users to each reviewer role. Changes will affect the review workflow.
            </div>
            
            <div>
              <Label className="text-xs font-medium">Responsible Engineer</Label>
              <Select 
                value={editReviewers.responsible_engineer || 'none'} 
                onValueChange={(v) => setEditReviewers({...editReviewers, responsible_engineer: v === 'none' ? '' : v})}
              >
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue placeholder="Select user..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Not Assigned --</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs font-medium">Discipline Reviewer</Label>
              <Select 
                value={editReviewers.discipline_reviewer || 'none'} 
                onValueChange={(v) => setEditReviewers({...editReviewers, discipline_reviewer: v === 'none' ? '' : v})}
              >
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue placeholder="Select user..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Not Assigned --</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs font-medium">Project Manager</Label>
              <Select 
                value={editReviewers.project_manager || 'none'} 
                onValueChange={(v) => setEditReviewers({...editReviewers, project_manager: v === 'none' ? '' : v})}
              >
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue placeholder="Select user..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Not Assigned --</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs font-medium">Document Controller</Label>
              <Select 
                value={editReviewers.document_controller || 'none'} 
                onValueChange={(v) => setEditReviewers({...editReviewers, document_controller: v === 'none' ? '' : v})}
              >
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue placeholder="Select user..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Not Assigned --</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditReviewersOpen(false)} className="flex-1" size="sm">
                Cancel
              </Button>
              <Button onClick={handleSaveReviewers} className="flex-1" size="sm">
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Revision Dialog (System Admin / Document Controller only) */}
      <Dialog open={editRevisionOpen} onOpenChange={(open) => {
        setEditRevisionOpen(open);
        if (!open) setSelectedCycleForRevisionEdit(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Revision Details</DialogTitle>
            <DialogDescription>
              Modify revision {selectedCycleForRevisionEdit?.revision} details
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditRevisionSubmit} className="space-y-4">
            <div>
              <Label>Revision Code</Label>
              <Input
                value={editRevisionData.revision}
                onChange={(e) => setEditRevisionData({...editRevisionData, revision: e.target.value})}
                placeholder="e.g., A, B, C, 01, 02"
              />
            </div>
            <div>
              <Label>Revision Date</Label>
              <Input
                type="date"
                value={editRevisionData.revision_date}
                onChange={(e) => setEditRevisionData({...editRevisionData, revision_date: e.target.value})}
              />
            </div>
            <div>
              <Label>Review Status</Label>
              <select
                value={editRevisionData.review_status}
                onChange={(e) => setEditRevisionData({...editRevisionData, review_status: e.target.value})}
                className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm"
              >
                <option value="open">Open</option>
                <option value="in_review">In Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <Label>Review Stage</Label>
              <select
                value={editRevisionData.review_stage}
                onChange={(e) => setEditRevisionData({...editRevisionData, review_stage: e.target.value})}
                className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm"
              >
                <option value="internal">Internal Review</option>
                <option value="pm_review">Responsible Engineer Review</option>
                <option value="dc_confirmation">DC Confirmation</option>
                <option value="sent_to_contractor">Sent to Contractor</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditRevisionOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReviewCycles;
